/**
 * UserList Component
 * Displays a list of users for a specific tenant with search and pagination
 */

import { makeStyles } from '@fluentui/react-components';
import type { TenantUser } from '../../types/user';
import type { OrganizationTenant } from '../../types/organization';
import { UserTable } from './UserTable';

interface UserListProps {
  tenantId: string;
  loading?: boolean;
  users?: TenantUser[];
  error?: string | null;
  onEditUser?: (user: TenantUser) => void;
  onDeleteUser?: (user: TenantUser) => void;
  onResetPassword?: (user: TenantUser) => void;
  selectedUsers?: Set<string>;
  onSelectAll?: (checked: boolean) => void;
  onSelectUser?: (userId: string, checked: boolean) => void;
  // Privilege management - role display only (role assignment moved to Edit User modal)
  currentUserIsSuperadmin?: boolean;
  // Column view configuration
  columnView?: 'full' | 'auth-only' | 'organization' | 'workspace';
  // Refresh trigger
  refreshTrigger?: number;
  // Passwordless status callback
  onPasswordlessCountsChange?: (readyCount: number, notReadyCount: number) => void;
  // Organization mode props
  organizationTenants?: OrganizationTenant[];
  organizationId?: number;
  onSaveTenantAccess?: (organizationId: number, userId: number, tenantIds: string[]) => Promise<void>;
}

const useStyles = makeStyles({
  errorContainer: {
    textAlign: 'center',
    padding: '40px',
    color: '#a4262c'
  }
});

export function UserList({
  tenantId,
  loading = false,
  users = [],
  error = null,
  onEditUser,
  onDeleteUser,
  onResetPassword,
  selectedUsers,
  onSelectAll,
  onSelectUser,
  currentUserIsSuperadmin = false,
  columnView = 'full',
  refreshTrigger = 0,
  onPasswordlessCountsChange,
  organizationTenants,
  organizationId,
  onSaveTenantAccess,
}: UserListProps) {
  const styles = useStyles();

  // Show error message
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p>{error}</p>
      </div>
    );
  }

  // Delegate to UserTable for all other states (loading, empty, data display)
  return (
    <UserTable
      users={users}
      tenantId={tenantId}
      loading={loading}
      onEdit={onEditUser}
      onDelete={onDeleteUser}
      onResetPassword={onResetPassword}
      selectedUsers={selectedUsers}
      onSelectAll={onSelectAll}
      onSelectUser={onSelectUser}
      currentUserIsSuperadmin={currentUserIsSuperadmin}
      columnView={columnView}
      refreshTrigger={refreshTrigger}
      onPasswordlessCountsChange={onPasswordlessCountsChange}
      organizationTenants={organizationTenants}
      organizationId={organizationId}
      onSaveTenantAccess={onSaveTenantAccess}
    />
  );
}
